import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import SPMActivityDialog from 'Components/SPMActivityDialog'
import type { ActivityData } from 'Components/SPMActivityDialog/SPMActivityDialog'
import SPMActivityDialogButton from 'Components/SPMActivityDialogButton'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import SPMAllowedApiClients from 'Services/ServiceProviderManagement/Features/AllowedClient/Components/SPMAllowedApiClients'
import SalesPointDetails from 'Services/ServiceProviderManagement/Features/SalesPoint/Components/SalesPointDetails'
import SalesPointDevices from 'Services/ServiceProviderManagement/Features/SalesPoint/Components/SalesPointDevices'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import { SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Pages/SalesPointListPage'
import {
  useGetSingleSalesPointQuery,
  useUpdateSingleSalesPointMutation
} from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import {
  type SalesPoint,
  SalesPointTabType
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'
import {
  ENABLE_SPM_DELETE_BUTTON,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY,
  SELECTED_PTA_STORAGE_KEY,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Button, Divider, Tab } from '@mui/material'
import { Box } from '@mui/system'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface SalesPointDrawerProps {
  open: boolean
  handleClose: () => void
  handleSalesPointDelete: () => void
  selectedSalesPointId: string | undefined
}

function SalesPointDrawer({
  open,
  handleClose,
  handleSalesPointDelete,
  selectedSalesPointId
}: Readonly<SalesPointDrawerProps>) {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/sales-points`
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const { copyObject } = useCopyObjectContext()

  const salesPointDetailsDrawerTabFromParams =
    searchParams.get(SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY) ?? SalesPointTabType.DETAILS
  const selectedPta = searchParams.get(PTA_PARAM_KEY) ?? undefined

  const { data: selectedSalesPoint, isFetching: isSingleSalesPointFetching } =
    useGetSingleSalesPointQuery(
      { sales_point_id: selectedSalesPointId ?? '', pta: selectedPta ?? '' },
      { skip: !selectedSalesPointId || !selectedPta }
    )
  const [updateSingleSalesPoint, { isLoading: isSubmitLoading }] =
    useUpdateSingleSalesPointMutation()

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>([])
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>([])

  // Sync local client state with server data during render (not in useEffect)
  // so the transfer list never mounts with stale values.
  const [prevSalesPoint, setPrevSalesPoint] = useState<SalesPoint | undefined>(undefined)

  if (!open && prevSalesPoint !== undefined) {
    // Reset tracking when drawer closes so reopening re-syncs
    setPrevSalesPoint(undefined)
  }

  if (open && selectedSalesPoint && selectedSalesPoint !== prevSalesPoint) {
    setPrevSalesPoint(selectedSalesPoint)
    setSelectedReadClients(selectedSalesPoint.client_read ?? [])
    setSelectedWriteClients(selectedSalesPoint.client_write ?? [])
  }

  const handleSalesPointDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: SalesPointTabType
  ) => {
    searchParams.set(SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleActivityDialogOpen = () => {
    setActivityDialogOpen(true)
  }

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false)
  }

  const handleActivityDialogSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    activityData: ActivityData
  ) => {
    e.preventDefault()
    if (!selectedSalesPoint || !selectedPta) return

    const salesPointData = {
      ...selectedSalesPoint,
      ...activityData
    } as SalesPoint

    updateSingleSalesPoint({
      ...salesPointData,
      sales_point_id: salesPointData.sales_point_id
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Sales point successfully saved.`)
        handleActivityDialogClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the sales point.`)
      })
  }

  const handleAllowedApiClientsSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedSalesPoint || !selectedPta) return

    const salesPointData = {
      ...selectedSalesPoint,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    updateSingleSalesPoint({
      ...salesPointData,
      sales_point_id: selectedSalesPoint.sales_point_id
    })
      .unwrap()
      .then(() => {
        navigate(
          appendQueryParamsToUrl(baseUrl, {
            [SELECTED_SALES_POINT_PARAMETER_KEY]: selectedSalesPoint.sales_point_id,
            [SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY]: SalesPointTabType.ALLOWED_API_CLIENTS,
            [PTA_PARAM_KEY]: selectedPta
          })
        )
        SnackbarUtils.success(t`Sales point successfully saved.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the sales point.`)
      })
  }

  const copyDataAndNavigateToCreate = () => {
    if (!selectedSalesPoint) return

    copyObject(CopyObjectKeys.SALES_POINT, selectedSalesPoint, [
      'sales_point_id',
      'pta',
      'activity_end_date'
    ])

    // Copied object's data can be applied to any PTA, clear PTA to avoid confusion
    IS_WALTTI_SERVICE_ENVIRONMENT && localStorage.removeItem(SELECTED_PTA_STORAGE_KEY)
    navigate(`${baseUrl}/create`)
  }

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          {selectedSalesPoint && (
            <SPMActivityDialogButton
              activityEndDate={selectedSalesPoint.activity_end_date?.toString()}
              isActive={selectedSalesPoint.active}
              isLoading={isSubmitLoading}
              onClick={handleActivityDialogOpen}
              sx={{ mr: 1 }}
            />
          )}
          {ENABLE_SPM_DELETE_BUTTON && (
            <DeleteButton
              sx={{ mr: 1 }}
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the sales point?`,
                  handleConfirm: () => {
                    handleSalesPointDelete()
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
          )}
          {selectedPta && selectedSalesPointId && (
            <CustomButton
              sx={{ mr: 1 }}
              onClick={() => navigate(`${baseUrl}/${selectedPta}/${selectedSalesPointId}/edit`)}
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          )}
          {selectedSalesPoint && (
            <CustomButton
              onClick={copyDataAndNavigateToCreate}
              size="small"
              startIcon={<ContentCopyIcon />}
            >{t`Use as template`}</CustomButton>
          )}
        </Box>
      }
    >
      <WithLoader loading={isSingleSalesPointFetching}>
        {selectedSalesPoint && (
          <TabContext value={salesPointDetailsDrawerTabFromParams}>
            <TabList
              aria-label={t`Sales point tab list`}
              onChange={handleSalesPointDetailsDrawerTabChange}
            >
              <Tab label={t`Details`} value={SalesPointTabType.DETAILS} />
              <Tab label={t`Devices`} value={SalesPointTabType.DEVICES} />
              <Tab label={t`Allowed API clients`} value={SalesPointTabType.ALLOWED_API_CLIENTS} />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={SalesPointTabType.DETAILS}>
              <SalesPointDetails salesPoint={selectedSalesPoint} />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={SalesPointTabType.DEVICES}>
              <SalesPointDevices salesPoint={selectedSalesPoint} />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={SalesPointTabType.ALLOWED_API_CLIENTS}>
              <StyledForm onSubmit={handleAllowedApiClientsSave} isDisabled={isSubmitLoading}>
                <SPMAllowedApiClients
                  selectedReadClients={selectedReadClients}
                  selectedWriteClients={selectedWriteClients}
                  setSelectedReadClients={setSelectedReadClients}
                  setSelectedWriteClients={setSelectedWriteClients}
                />
                <Box
                  sx={{
                    mt: 2,
                    mx: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    columnGap: 2
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      color="inherit"
                      sx={{ width: '100%' }}
                    >
                      {t`Cancel`}
                    </Button>
                    <CustomLoadingButton
                      isLoading={isSubmitLoading}
                      type="submit"
                      sx={{ width: '100%' }}
                      icon={<SaveIcon />}
                      iconPosition="start"
                    >
                      {t`Save changes`}
                    </CustomLoadingButton>
                  </Box>
                </Box>
              </StyledForm>
            </TabPanel>
          </TabContext>
        )}
      </WithLoader>
      {selectedSalesPoint && (
        <SPMActivityDialog
          open={activityDialogOpen}
          handleClose={handleActivityDialogClose}
          originalActivityEndDate={selectedSalesPoint.activity_end_date?.toString()}
          isActive={selectedSalesPoint.active}
          onSubmit={handleActivityDialogSubmit}
          spmObjectName={t`sales point`}
          isSubmitLoading={isSubmitLoading}
          originalAdditionalInfo={selectedSalesPoint.additional_info}
        />
      )}
    </DrawerRight>
  )
}

export default SalesPointDrawer
